import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { DataAccessFairsFacade } from '@planone/bau365/data-access-fair-data';
import {
  ApiBusinessCard,
  ApiBusinessCardImage,
  ApiJobInformation,
  BusinessCard,
  JobInformation,
  transformApiJobInformation,
  transformBusinessCard,
  transformJobInformation,
} from '@planone/bau365/util-user-data';
import { CursorInterface, CursorPaginatedResponse } from '@planone/shared/util-api-cursor-handling';
import { KeycloakService } from 'keycloak-angular';
import { Observable, of, switchMap, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';

import { ProfessionApiResponse, ProfessionApiResponseElement, SynonymsResponse } from './user-response.models';

import { USER_SERVICE_CONFIG, UserServiceConfigInterface } from '../../config/user-service.config.interface';
import {
  ProductGroup,
  Profession,
  SpecializationSynonyms,
  Synonym,
  Topic,
} from '../../state/user/data-access-user.models';

@Injectable()
export class UserService {
  userApiURL: string | null = null;
  onboardingApiURL: string | null = null;
  keycloakApiURL: string | null = null;

  fairId$: Observable<string>;

  constructor(
    private httpClient: HttpClient,
    private keycloakService: KeycloakService,
    private fairsFacade: DataAccessFairsFacade,
    @Inject(USER_SERVICE_CONFIG) userServiceConfig: UserServiceConfigInterface
  ) {
    this.keycloakApiURL = userServiceConfig.keycloakApiURL;
    this.userApiURL = userServiceConfig.userServiceApiURL;
    this.onboardingApiURL = userServiceConfig.fairDataGatewayURL;
    this.fairId$ = this.fairsFacade.fairId$;
  }

  private getUserId(): string | null {
    return this.keycloakService.getKeycloakInstance()?.tokenParsed?.sub ?? null;
  }
  getAvailableProfessions(fairId: string): Observable<
    {
      professions: Profession[];
    } & CursorInterface
  > {
    return this.httpClient
      .get<CursorPaginatedResponse<ProfessionApiResponse>>(`${this.onboardingApiURL}/fairs/${fairId}/professions`)
      .pipe(
        map((response) => ({
          cursors: response.cursors,
          professions: response.items.map((profession) => this.mapProfessionToFrontend(profession)),
        }))
      );
  }

  getAvailableSynonyms(): Observable<SpecializationSynonyms[]> {
    return this.fairId$.pipe(
      switchMap((fairId) =>
        this.httpClient
          .get<SynonymsResponse>(`${this.userApiURL}/fairs/${fairId}/professional-information/synonyms`)
          .pipe(
            map((response) =>
              Object.keys(response).map((specialization) => ({
                specialization,
                profession: response[specialization].profession_id,
                label: response[specialization].name,
                synonyms: response[specialization].synonyms,
              }))
            )
          )
      )
    );
  }

  private mapProfessionToFrontend(responseItem: ProfessionApiResponse) {
    const { basic, specialized } = responseItem;
    const { id, external_id, name } = basic;
    const profession: Profession = {
      id,
      external_id,
      label: name,
      specializations: this.mapProfessionSpecializationsToFrontend(specialized),
    };
    return profession;
  }

  private mapProfessionSpecializationsToFrontend(specialized: ProfessionApiResponseElement[]) {
    return specialized.map((specialization) => {
      const { id, external_id, name } = specialization;
      return {
        id,
        external_id,
        label: name,
      };
    });
  }

  getAvailableTopics(
    fairId: string,
    nextCursor?: string
  ): Observable<
    {
      topics: Topic[];
    } & CursorInterface
  > {
    let requestParams: HttpParams = new HttpParams();
    if (nextCursor != null) {
      requestParams = requestParams.set('cursor', nextCursor);
    }

    return this.httpClient
      .get<
        CursorPaginatedResponse<{
          id: string;
          name: string;
          topic_type: string;
        }>
      >(`${this.onboardingApiURL}/fairs/${fairId}/topics`, { params: requestParams })
      .pipe(
        map((response) => {
          return {
            cursors: response.cursors,
            topics: response.items.map((responseItem) => {
              return { id: responseItem.id, label: responseItem.name, topic_type: responseItem.topic_type };
            }),
          };
        })
      );
  }

  getAvailableProductGroups(
    fairId: string,
    maxLevels: number,
    nextCursor?: string,
    limit?: number
  ): Observable<
    {
      productGroups: ProductGroup[];
    } & CursorInterface
  > {
    let requestParams: HttpParams = new HttpParams();
    if (limit) {
      requestParams = requestParams.set('limit', `${limit}`);
    }
    if (maxLevels) {
      requestParams = requestParams.set('max_levels', `${maxLevels}`);
    }
    if (nextCursor) {
      requestParams = requestParams.set('cursor', nextCursor);
    }

    return this.httpClient
      .get<
        CursorPaginatedResponse<{
          id: string;
          name: string;
          root_path: [];
          external_id: string;
        }>
      >(`${this.onboardingApiURL}/fairs/${fairId}/product-groups`, { params: requestParams })
      .pipe(
        map((response) => {
          return {
            cursors: response.cursors,
            productGroups: response.items.map((responseItem) => {
              return {
                id: responseItem.id,
                label: responseItem.name,
                root_path: responseItem.root_path,
                external_id: responseItem.external_id,
              };
            }),
          };
        })
      );
  }

  setProfessionSpecialization(
    profession: string | undefined,
    specialization: string | undefined,
    synonym: Synonym | undefined
  ) {
    const user_id = this.getUserId();
    if (user_id == null) {
      return throwError(() => new Error('Login required'));
    }
    return this.httpClient.post<{
      professions: string[];
      specializations: string[];
      synonyms?: Synonym[];
    }>(`${this.userApiURL}/users/${user_id}/professional-information`, {
      professions: [profession],
      specializations: [specialization],
      synonyms: synonym ? [synonym] : undefined,
    });
  }

  getUserProfile() {
    const user_id = this.getUserId();
    if (user_id == null) {
      return throwError(() => new Error('Login required'));
    }
    return this.httpClient.get<{
      title: string;
      description: string;
    }>(`${this.userApiURL}/users/${user_id}/personal-information`);
  }

  setProfile(title: string, shortDescription: string) {
    const user_id = this.getUserId();
    if (user_id == null) {
      return throwError(() => new Error('Login required'));
    }

    return this.httpClient.post<{
      title: string;
      description: string;
    }>(`${this.userApiURL}/users/${user_id}/personal-information`, {
      title: title ? title : undefined,
      description: shortDescription ? shortDescription : undefined,
    });
  }

  setKeycloakData(givenName: string, familyName: string) {
    if (this.getUserId() == null) {
      return throwError(() => new Error('Login required'));
    }

    return this.httpClient
      .get<{
        firstName: string;
        lastName: string;
      }>(`${this.keycloakApiURL}/realms/bau365/account/`, {})
      .pipe(
        switchMap((completeUserProfile) => {
          completeUserProfile.firstName = givenName;
          completeUserProfile.lastName = familyName;
          return this.httpClient.post(`${this.keycloakApiURL}/realms/bau365/account/`, completeUserProfile);
        }),
        tap(() => {
          this.keycloakService.updateToken(-1);
        })
      );
  }

  setJobInformation(jobInformation: JobInformation): Observable<JobInformation> {
    const user_id = this.getUserId();
    if (user_id == null) {
      return throwError(() => new Error('Login required'));
    }
    const apiJobInformation = transformApiJobInformation(jobInformation);
    return this.httpClient
      .post<ApiJobInformation>(`${this.userApiURL}/users/${user_id}/job-information`, apiJobInformation)
      .pipe(map(transformJobInformation));
  }

  getUserJobInformation(): Observable<JobInformation> {
    const user_id = this.getUserId();
    if (user_id == null) {
      return throwError(() => new Error('Login required'));
    }
    return this.httpClient
      .get<ApiJobInformation>(`${this.userApiURL}/users/${user_id}/job-information`)
      .pipe(map(transformJobInformation));
  }

  getUserProfessionalInformation() {
    return this.httpClient.get<{
      professions: string[];
      specializations: string[];
      synonyms?: {
        id?: string;
        label: string;
      }[];
    }>(`${this.userApiURL}/users/${this.getUserId()}/professional-information`);
  }

  getUserInterests() {
    return this.httpClient.get<{
      product_groups: string[];
      topics: string[];
    }>(`${this.userApiURL}/users/${this.getUserId()}/interests`);
  }

  requestNewsletterSignup(email: string) {
    return this.httpClient.post<{
      email: string;
    }>(`${this.userApiURL}/users/newsletter`, {
      email: email,
    });
  }

  deleteAccount(predefinedReason: string, otherReason: string) {
    const user_id = this.getUserId();
    if (user_id == null) {
      return throwError(() => new Error('Login required'));
    }

    return this.httpClient.post<{
      predefinedReason: string;
      otherReason: string;
    }>(`${this.userApiURL}/users/delete-account`, {
      predefined_reason: predefinedReason ? predefinedReason : undefined,
      other_reason: otherReason ? otherReason : undefined,
    });
  }

  getBusinessCard(): Observable<BusinessCard | undefined> {
    return this.httpClient.get<ApiBusinessCard>(`${this.userApiURL}/business-cards`).pipe(
      map((response) => transformBusinessCard(response)),
      catchError((error) => {
        if (error.status === 404) {
          return of(undefined);
        }
        throw error;
      })
    );
  }

  createBusinessCard(): Observable<BusinessCard> {
    return this.httpClient
      .post<ApiBusinessCard>(`${this.userApiURL}/business-cards`, {})
      .pipe(map((response) => transformBusinessCard(response)));
  }

  deleteBusinessCard(): Observable<boolean> {
    return this.httpClient.delete<void>(`${this.userApiURL}/business-cards`, {}).pipe(map(() => true));
  }

  setProfileImage(file: File): Observable<ApiBusinessCardImage> {
    const formData = new FormData();
    formData.append('file', file);
    return this.httpClient
      .post<ApiBusinessCardImage>(`${this.userApiURL}/business-cards/image`, formData)
      .pipe(map((response) => response));
  }

  deleteProfileImage(): Observable<File> {
    return this.httpClient
      .delete<File>(`${this.userApiURL}/business-cards/image`, {})
      .pipe(map((response) => response));
  }

  sendBusinessCard(fairId: string, exhibitorId: string): Observable<boolean> {
    return this.httpClient
      .post(`${this.userApiURL}/companies/fairs/${fairId}/exhibitors/${exhibitorId}/request-a-call-back`, undefined)
      .pipe(map(() => true));
  }
}
